import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import ProfileLawyer from './ProfileLawyer';
import ProfileClient from './ProfileClient';
import { supabase } from '../supabaseClient';
import './Profile.css';

export default function Profile({ userRole, user, onNavigate }) {
    const [loading, setLoading] = useState(true);
    const [profileData, setProfileData] = useState(null);

    useEffect(() => {
        if (user?.id) loadProfile();
    }, [user?.id]);

    const loadProfile = async () => {
        if (!user?.id) return;

        try {
            const { data, error } = await supabase
                .from('user_profiles')
                .select('*')
                .eq('id', user.id)
                .single();

            if (error) console.error('Profile load error:', error);

            if (data) {
                let specs = [];
                if (data.specialization) {
                    try {
                        specs = data.specialization.startsWith('[')
                            ? JSON.parse(data.specialization)
                            : data.specialization.split(', ').filter(Boolean);
                    } catch { specs = []; }
                }

                setProfileData({
                    email: data.email,
                    name: data.full_name || '',
                    phone: data.phone || '',
                    address: data.address || '',
                    city: data.city || '',
                    state: data.state || '',
                    zip: data.zip || '',
                    bio: data.bio || '',
                    photo_path: data.photo_url || '',
                    ...(userRole === 'lawyer' ? {
                        bar_license: data.bar_number || '',
                        specializations: specs,
                        price_min: data.price_min || '',
                        price_max: data.price_max || '',
                        experience: data.experience ? parseInt(data.experience) || 0 : 0,
                        case_histories: data.case_histories
                            ? (Array.isArray(data.case_histories)
                                ? data.case_histories
                                : JSON.parse(data.case_histories))
                            : [],
                        is_listed_advocate: data.is_listed_advocate || 0,
                    } : {})
                });
            } else {
                setProfileData({
                    email: user.email,
                    name: user.name || '',
                    phone: '', address: '', city: '', state: '', zip: '',
                    bio: '', photo_path: '',
                    ...(userRole === 'lawyer' ? {
                        bar_license: '', specializations: [],
                        experience: 0, case_histories: [], is_listed_advocate: 0,
                    } : {})
                });
            }
        } catch (error) {
            console.error('Error loading profile:', error);
            setProfileData({
                email: user.email, name: user.name || '',
                phone: '', address: '', city: '', state: '', zip: '',
                bio: '', photo_path: '',
                ...(userRole === 'lawyer' ? {
                    bar_license: '', specializations: [],
                    experience: 0, case_histories: [], is_listed_advocate: 0,
                } : {})
            });
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async (updatedData) => {
        if (!user?.id) return { success: false, error: 'Not logged in' };
        try {
            // All fields — both client and lawyer
            const supaData = {
                full_name: updatedData.name || null,
                phone: updatedData.phone || null,
                address: updatedData.address || null,
                city: updatedData.city || null,
                state: updatedData.state || null,
                zip: updatedData.zip || null,
                bio: updatedData.bio || null,
                photo_url: updatedData.photo_path || null,
                updated_at: new Date().toISOString(),
            };

            if (userRole === 'lawyer') {
                supaData.bar_number = updatedData.bar_license || null;
                supaData.experience = updatedData.experience ? String(updatedData.experience) : null;
                supaData.specialization = updatedData.specializations?.length
                    ? JSON.stringify(updatedData.specializations)
                    : null;
                supaData.price_min = updatedData.price_min ? parseInt(updatedData.price_min) : null;
                supaData.price_max = updatedData.price_max ? parseInt(updatedData.price_max) : null;
            }

            const { error } = await supabase
                .from('user_profiles')
                .update(supaData)
                .eq('id', user.id);

            if (error) throw error;

            await loadProfile();
            return { success: true };
        } catch (error) {
            console.error('Error saving profile:', error);
            return { success: false, error: error.message };
        }
    };

    if (loading) {
        return (
            <div className="profile-loading">
                <div className="loading-spinner"></div>
                <p>Loading profile...</p>
            </div>
        );
    }

    return (
        <div className="profile-container">
            {userRole === 'lawyer' ? (
                <ProfileLawyer
                    profileData={profileData}
                    onSave={handleSave}
                    onNavigate={onNavigate}
                    user={user}
                />
            ) : (
                <ProfileClient
                    profileData={profileData}
                    onSave={handleSave}
                    onNavigate={onNavigate}
                />
            )}
        </div>
    );
}